import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import DraggableModal from "./DraggableModal";
import EmailDialog from "./EmailDialog";
import MassBalancePanel from "./organicAudit/MassBalancePanel";
import OrganicCertsPanel from "./organicAudit/OrganicCertsPanel";
import TraceabilityPanel from "./organicAudit/TraceabilityPanel";
import OrganicLabelsPanel from "./organicAudit/OrganicLabelsPanel";
import GeneralInfoPanel from "./organicAudit/GeneralInfoPanel";
import TraceabilityProcedurePanel from "./organicAudit/TraceabilityProcedurePanel";
import AuditComments from "./organicAudit/AuditComments";
import AuditSummaryPanel from "./organicAudit/AuditSummaryPanel";
import ComplaintsProcedurePanel from "./organicAudit/ComplaintsProcedurePanel";
import {
  saveReportBlob, loadReportBlob, deleteReportBlob, loadReportBlobs,
  saveDocBlob, loadDocBlob, deleteDocBlob, loadDocBlobs, openBlobInTab,
} from "./organicAudit/auditStorage";
import { isAdmin as profileIsAdmin, verifyCurrentUserPassword } from "../userProfile";
import { saveToSupabase, loadFromSupabase, saveManyToSupabase, deleteFromSupabase } from "../supabaseDB";
import { appendDisclosureByAccount } from "./emailHelpers";

/* ============================================================================
 * Organic Audit Registry
 *
 * Permanent record of every USDA organic audit. Each audit folder
 * carries (a) a structured request log — every report Arturo
 * generated for the auditor, with the params used and an
 * "exact-as-sent" PDF snapshot — and (b) an "Other Documents"
 * shelf for hand-uploaded PDFs (pre-audit packet, files the
 * auditor returned, etc).
 *
 * Open / Closed state machine:
 *   - New audits start `open`. Edits, deletes, new reports, doc
 *     uploads are all enabled.
 *   - "Close Audit" stamps closedAt, freezes the audit (read-only
 *     in the UI), and writes the inspection-date / auditor /
 *     completion fields back to GFI's own organic cert in
 *     gfi-certifications (LS + Supabase dual-write).
 *   - "Reopen Audit" is admin-only and gated by a fresh password
 *     re-check via verifyCurrentUserPassword(). Reopen flips
 *     status back to `open` and stamps reopenedAt, but leaves the
 *     cert's inspectionDate alone (the auditor did inspect the
 *     site; reopening doesn't undo that fact).
 *   - "Delete Audit" is only available while open. Two confirms
 *     because audits are supposed to be a permanent trail and we
 *     don't want a misclick wiping years of compliance history.
 *
 * Storage split:
 *   localStorage `gfi-organic-audits` → header + reports[] metadata
 *                                       + otherDocs[] metadata
 *   IndexedDB    audit-report-blobs   → { entryId: Blob } snapshots
 *   IndexedDB    audit-doc-blobs      → { docId: Blob }   uploads
 *
 * Report entry shape (persisted in audit.reports[]):
 *   { id, type, title, generatedAt, recipients[], params,
 *     hasBlob?, filename?, mime? }
 *
 * Other-doc shape (persisted in audit.otherDocs[]):
 *   { id, name, category: 'pre'|'received'|'other',
 *     uploadedAt, fileName, fileSize, fileType }
 * ========================================================================= */

const LS_KEY = "gfi-organic-audits";

const T = {
  en: {
    title: "Organic Audit",
    sub: "Permanent record of every USDA organic audit",
    btnNew: "+ New Audit",
    colName: "Name", colYear: "Year", colAuditor: "Auditor",
    colStart: "Start Date", colReports: "Reports Generated", colStatus: "Status",
    empty: "No audits yet. Click \"+ New Audit\" to create the first one.",
    statusOpen: "Open", statusClosed: "Closed",
    /* Form */
    formNew: "New Audit", formEdit: "Edit Audit",
    fName: "Audit Name", fYear: "Year",
    fAuditorName: "Auditor Name", fAuditorEmail: "Auditor Email",
    fStart: "Start Date",
    save: "Save", cancel: "Cancel", close: "Close", edit: "Edit",
    delete: "Delete Audit",
    /* Detail */
    reports: "Reports",
    repMass:  "Mass Balance",
    repMassSub: "Inbounds vs Outbounds summary for the organic-certified flow",
    repCerts: "Organic Certificates",
    repCertsSub: "Executive list + every selected certificate PDF appended",
    repTrace: "Traceability Sample",
    repTraceSub: "Chain of custody for a single shipment, with all source documents",
    repLabels: "Organic Labels",
    repLabelsSub: "Photo sheet of the labels in use for the organic SKUs",
    repGenInfo: "General Information & Flow Diagram",
    repGenInfoSub: "NOP 205.201 & 205.401 — operation profile and flow diagram",
    repTrace2: "Traceability Procedure",
    repTrace2Sub: "Labeling and product traceability procedure",
    repComplaints: "Customer Complaint Procedure",
    repComplaintsSub: "Receiving, tracing and resolving distributor complaints",
    generate: "Open Report",
    closedNoGenerate: "Audit is closed — reopen it to generate new reports",
    historyTitle: "Auditor Requests",
    historyEmpty: "No reports generated yet for this audit.",
    historyTime: "Generated",
    historyRecipients: "Sent to",
    historyParams: "Parameters",
    actReopen: "Reopen",
    actView: "View PDF",
    actDownload: "Download",
    actEmail: "Email",
    actDelete: "Delete",
    actReconfigure: "Reconfigure",
    noSnapshot: "no snapshot",
    /* Bulk email */
    emailSelected: "Email Selected",
    emailWholeAudit: "Email Whole Audit",
    nothingToEmail: "This audit has no reports or documents yet. Generate at least one report before sending.",
    bulkEmailSubject: "Audit Package",
    /* Other documents */
    docsTitle: "Other Documents",
    docsEmpty: "No documents uploaded yet. Drag PDFs here or click Upload.",
    docsEmptyClosed: "No documents uploaded. The audit is closed — no new uploads allowed.",
    docsUpload: "Upload",
    docsDrag: "Drop PDF files here",
    docsName: "Document name",
    docsCategory: "Category",
    cat_pre: "Pre-Audit", cat_received: "Received from Auditor", cat_other: "Other",
    /* Close / reopen */
    btnClose: "Close Audit",
    btnReopen: "Reopen Audit",
    closeAuditTitle: "Close this audit?",
    closeAuditMsg: "Once closed, the audit becomes read-only. No edits, new reports, or document uploads will be allowed. This will also stamp the inspection date + auditor name onto GFI's organic certificate.",
    closeAuditConfirm: "Yes, close audit",
    reopenAuditTitle: "Reopen this audit?",
    reopenAuditMsg: "Reopening restores edit access for this audit. Enter your password to confirm.",
    reopenAuditConfirm: "Confirm + Reopen",
    passwordLabel: "Your password",
    badPassword: "Password did not match.",
    onlyAdminCanReopen: "Only administrators can reopen a closed audit.",
    closedBadgeTitle: "This audit is closed. Read-only.",
    /* Delete confirms */
    deleteAuditTitle: "Delete this audit?",
    deleteAuditMsg1: "This will permanently remove the audit, every generated report, and every uploaded document. This cannot be undone.",
    deleteAuditMsg2: "Are you absolutely sure? Type DELETE to confirm.",
    deleteAuditConfirm: "DELETE",
    /* GFI cert strip */
    gfiCertTitle: "GFI Organic Certificate",
    gfiCertNumber: "Cert #",
    gfiCertNop: "NOP #",
    gfiCertCertifier: "Certifier",
    gfiCertValid: "Valid",
    gfiCertMissing: "No active GFI organic certificate on file. Create one in Compliance → Certifications.",
    gfiCertLastInsp: "Last Inspection",
    /* Misc */
    none: "—",
    confirmDelete: "Delete this entry? This cannot be undone.",
    onlyPdf: "Only PDF files are supported.",
    selectedCount: "selected",
  },
  es: {
    title: "Auditoría Orgánica",
    sub: "Registro permanente de cada auditoría orgánica USDA",
    btnNew: "+ Nueva Auditoría",
    colName: "Nombre", colYear: "Año", colAuditor: "Auditor",
    colStart: "Fecha de inicio", colReports: "Reportes generados", colStatus: "Estado",
    empty: "Sin auditorías aún. Haz clic en \"+ Nueva Auditoría\" para crear la primera.",
    statusOpen: "Abierta", statusClosed: "Cerrada",
    formNew: "Nueva Auditoría", formEdit: "Editar Auditoría",
    fName: "Nombre de la auditoría", fYear: "Año",
    fAuditorName: "Nombre del auditor", fAuditorEmail: "Correo del auditor",
    fStart: "Fecha de inicio",
    save: "Guardar", cancel: "Cancelar", close: "Cerrar", edit: "Editar",
    delete: "Eliminar Auditoría",
    reports: "Reportes",
    repMass:  "Balance de Masas",
    repMassSub: "Resumen de entradas vs salidas para el flujo orgánico certificado",
    repCerts: "Certificados Orgánicos",
    repCertsSub: "Lista ejecutiva + cada certificado PDF seleccionado anexado",
    repTrace: "Muestra de Trazabilidad",
    repTraceSub: "Cadena de custodia para un embarque, con todos los documentos fuente",
    repLabels: "Etiquetas Orgánicas",
    repLabelsSub: "Hoja de fotos de las etiquetas en uso para los SKUs orgánicos",
    repGenInfo: "Información General y Diagrama de Flujo",
    repGenInfoSub: "NOP 205.201 & 205.401 — perfil de la operación y diagrama de flujo",
    repTrace2: "Procedimiento de Trazabilidad",
    repTrace2Sub: "Procedimiento de etiquetado y trazabilidad de producto",
    repComplaints: "Procedimiento de Quejas de Clientes",
    repComplaintsSub: "Recepción, trazabilidad y resolución de quejas de distribuidores",
    generate: "Abrir Reporte",
    closedNoGenerate: "La auditoría está cerrada — reábrela para generar nuevos reportes",
    historyTitle: "Solicitudes del Auditor",
    historyEmpty: "Aún no se han generado reportes para esta auditoría.",
    historyTime: "Generado",
    historyRecipients: "Enviado a",
    historyParams: "Parámetros",
    actReopen: "Reabrir",
    actView: "Ver PDF",
    actDownload: "Descargar",
    actEmail: "Enviar",
    actDelete: "Eliminar",
    actReconfigure: "Reconfigurar",
    noSnapshot: "sin snapshot",
    emailSelected: "Enviar Seleccionados",
    emailWholeAudit: "Enviar Auditoría Completa",
    nothingToEmail: "Esta auditoría aún no tiene reportes ni documentos. Genera al menos un reporte antes de enviar.",
    bulkEmailSubject: "Paquete de Auditoría",
    docsTitle: "Otros Documentos",
    docsEmpty: "Sin documentos cargados aún. Arrastra PDFs aquí o haz clic en Subir.",
    docsEmptyClosed: "Sin documentos cargados. La auditoría está cerrada — no se permiten cargas nuevas.",
    docsUpload: "Subir",
    docsDrag: "Suelta archivos PDF aquí",
    docsName: "Nombre del documento",
    docsCategory: "Categoría",
    cat_pre: "Previo a Auditoría", cat_received: "Recibido del Auditor", cat_other: "Otro",
    btnClose: "Cerrar Auditoría",
    btnReopen: "Reabrir Auditoría",
    closeAuditTitle: "¿Cerrar esta auditoría?",
    closeAuditMsg: "Al cerrarse, la auditoría queda en modo solo-lectura. No se permitirán ediciones, nuevos reportes o cargas de documentos. También se estampará la fecha de inspección + nombre del auditor en el certificado orgánico de GFI.",
    closeAuditConfirm: "Sí, cerrar auditoría",
    reopenAuditTitle: "¿Reabrir esta auditoría?",
    reopenAuditMsg: "Reabrir restaura el acceso de edición. Ingresa tu contraseña para confirmar.",
    reopenAuditConfirm: "Confirmar + Reabrir",
    passwordLabel: "Tu contraseña",
    badPassword: "Contraseña incorrecta.",
    onlyAdminCanReopen: "Solo los administradores pueden reabrir una auditoría cerrada.",
    closedBadgeTitle: "Esta auditoría está cerrada. Solo lectura.",
    deleteAuditTitle: "¿Eliminar esta auditoría?",
    deleteAuditMsg1: "Esto eliminará permanentemente la auditoría, todos los reportes generados y todos los documentos cargados. No se puede deshacer.",
    deleteAuditMsg2: "¿Estás absolutamente seguro? Escribe ELIMINAR para confirmar.",
    deleteAuditConfirm: "ELIMINAR",
    gfiCertTitle: "Certificado Orgánico GFI",
    gfiCertNumber: "Cert #",
    gfiCertNop: "NOP #",
    gfiCertCertifier: "Certificadora",
    gfiCertValid: "Vigente",
    gfiCertMissing: "Sin certificado orgánico GFI activo. Créalo en Cumplimiento → Certificaciones.",
    gfiCertLastInsp: "Última Inspección",
    none: "—",
    confirmDelete: "¿Eliminar esta entrada? No se puede deshacer.",
    onlyPdf: "Solo se soportan archivos PDF.",
    selectedCount: "seleccionados",
  },
};

const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 };
const btnDanger = { padding: "8px 16px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.30)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnTiny = { padding: "3px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10.5, fontWeight: 600 };

/* localStorage now serves as a synchronous cache only — source of
   truth for organic_audits moved to Supabase. The component mounts
   with whatever LS has (instant render), then re-fetches from
   Supabase in the background and replaces the in-memory list.
   Every mutation mirrors the full audit set to Supabase via
   saveManyToSupabase so a row that already exists is upserted and a
   new row gets inserted on the same call. Deletes hit
   deleteFromSupabase directly via deleteAuditFromSupabase below. */
function loadAudits() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch (e) { return []; }
}
async function loadAuditsFromSupabase() {
  var lsAudits = loadAudits();
  try {
    var rows = await loadFromSupabase("organic_audits", { force: true });
    if (Array.isArray(rows) && rows.length > 0) {
      try { localStorage.setItem(LS_KEY, JSON.stringify(rows)); } catch (e) {}
      return rows;
    }
    if (lsAudits.length > 0) {
      console.log("[audits] Supabase empty, migrating", lsAudits.length, "audit(s) from localStorage");
      await saveManyToSupabase("organic_audits", lsAudits).catch(function (e) {
        console.warn("[audits] migration to Supabase failed:", e && e.message);
      });
      return lsAudits;
    }
    return [];
  } catch (e) {
    console.warn("[audits] Supabase load failed, falling back to LS:", e && e.message);
    return lsAudits;
  }
}
function saveAudits(arr) {
  /* LS cache write — synchronous so the rest of the registry's
     legacy code paths see the latest state without an await. */
  try { localStorage.setItem(LS_KEY, JSON.stringify(arr || [])); } catch (e) { console.warn("[audits] LS save failed:", e && e.message); }
  /* Mirror to Supabase. Fire-and-forget; bulk upsert handles both
     new + existing rows in a single round trip. Failures keep the
     LS state intact so the user doesn't lose work. */
  if (Array.isArray(arr) && arr.length > 0) {
    saveManyToSupabase("organic_audits", arr).catch(function (e) {
      console.warn("[audits] Supabase save failed (records still in LS):", e && e.message);
    });
  }
}
function deleteAuditFromSupabase(id) {
  return deleteFromSupabase("organic_audits", id).catch(function (e) {
    console.warn("[audits] Supabase delete failed:", e && e.message);
  });
}

function isValidEmail(s) {
  if (typeof s !== "string") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }
  catch (e) { return d; }
}
function fmtDateTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
}
function fmtFileSize(bytes) {
  if (!bytes || bytes < 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1024 / 1024).toFixed(1) + " MB";
}
function newId() {
  return Date.now() + "-" + Math.floor(Math.random() * 1e9);
}

/* Normalize audit.status so the rest of the UI can branch on just
   'open' / 'closed'. Legacy records used 'in_progress' / 'completed'. */
function normalizeStatus(s) {
  if (s === "closed" || s === "completed") return "closed";
  return "open";
}

/* ----------------------------------------------------------------------
 * GFI organic certificate helpers
 *
 * The Compliance → Certifications module stores every cert under
 * gfi-certifications. GFI's own org cert (PA-OR-1186) is the one row
 * whose entityType is 'gfi' and certType is 'organic'. We surface it
 * read-only in the audit header, and on Close we stamp the audit
 * info back onto that row so the cert always knows when it was last
 * inspected (and by whom).
 * -------------------------------------------------------------------- */
function loadGfiOrganicCert() {
  try {
    const all = JSON.parse(localStorage.getItem("gfi-certifications") || "[]");
    return all.find(function (c) { return c && c.entityType === "gfi" && c.certType === "organic"; }) || null;
  } catch (e) { return null; }
}

async function updateGfiCertWithAuditClose(audit) {
  const cert = loadGfiOrganicCert();
  if (!cert) return null;
  /* Inspection date defaults to the audit's startDate (when the
     auditor actually visited) and only falls back to "today" if the
     audit was created without one. */
  const inspectionDate = audit.startDate || new Date().toISOString().substring(0, 10);
  const patched = {
    ...cert,
    inspectionDate: inspectionDate,
    lastAuditId: audit.id,
    lastAuditorName: audit.auditorName || "",
    lastAuditorEmail: audit.auditorEmail || "",
    auditCompletedAt: new Date().toISOString(),
  };
  /* Mirror into localStorage immediately so other modules read the
     new state without waiting for a Supabase round-trip. */
  try {
    const all = JSON.parse(localStorage.getItem("gfi-certifications") || "[]");
    const next = all.map(function (c) { return c.id === cert.id ? patched : c; });
    localStorage.setItem("gfi-certifications", JSON.stringify(next));
  } catch (e) { /* logged below if SB fails too */ }
  /* Dual-write to Supabase. The unknown fields land in the cert row's
     `extra` JSONB column via supabaseDB's payload shaper. Failure is
     non-fatal — LS is the source of truth and the next cert-registry
     refresh will re-sync. */
  try {
    await saveToSupabase("certifications", patched);
  } catch (e) {
    console.warn("[audit-close] failed to write cert update to Supabase:", e && e.message);
  }
  return patched;
}

/* ----------------------------------------------------------------------
 * Param summary — human-readable one-liner so the history row shows
 * what the report was actually built with (date range, lot, # certs,
 * etc) without forcing the user to expand a panel.
 * -------------------------------------------------------------------- */
function paramSummary(type, params, lang) {
  const ES = lang === "es";
  if (!params) return "";
  if (type === "mass_balance") {
    const com = params.commodity && params.commodity !== "ALL" ? params.commodity : (ES ? "Todos orgánicos" : "All organic");
    const range = (params.dateFrom || "") + " → " + (params.dateTo || "");
    return com + "  ·  " + range;
  }
  if (type === "organic_certs") {
    const n = Array.isArray(params.selectedCertIds) ? params.selectedCertIds.length : 0;
    return n + (ES ? " certificado(s) incluidos" : " certificate(s) included");
  }
  if (type === "traceability") {
    return (ES ? "Embarque " : "Shipment ") + (params.shipmentId || "—") + (params.invoiceId ? (ES ? "  ·  Factura " : "  ·  Invoice ") + params.invoiceId : "");
  }
  if (type === "organic_labels") {
    const n = Array.isArray(params.selectedProductIds) ? params.selectedProductIds.length : 0;
    return n + " SKUs";
  }
  return "";
}

function Field({ label, children, error }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
      {children}
      {error && <div style={{ color: "#DC2626", fontSize: 11, marginTop: 3 }}>{error}</div>}
    </div>
  );
}

function StatusPill({ status, t }) {
  const isClosed = normalizeStatus(status) === "closed";
  const style = isClosed
    ? { background: "rgba(107,114,128,0.15)", color: "#374151", border: "1px solid rgba(107,114,128,0.30)" }
    : { background: "rgba(16,185,129,0.12)", color: "#047857", border: "1px solid rgba(16,185,129,0.30)" };
  const label = isClosed ? t.statusClosed : t.statusOpen;
  return <span title={isClosed ? t.closedBadgeTitle : undefined} style={{ ...style, display: "inline-block", padding: "2px 10px", borderRadius: 10, fontSize: 10, fontWeight: 700, letterSpacing: "0.3px" }}>{label}</span>;
}

export default function OrganicAuditRegistry({ lang }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [audits, setAudits] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  /* Async admin probe — drives the Reopen-button visibility. Default
     `false` so non-admins never see Reopen even before the probe
     resolves. */
  const [isAdmin, setIsAdmin] = useState(false);
  /* { id, type } modal payloads for the close / reopen / delete
     confirmations. Keeping them out of AuditDetail so the modal can
     stay open as the parent unmounts a closing dialog (no race). */
  const [confirmClose, setConfirmClose] = useState(null);
  const [confirmReopen, setConfirmReopen] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    /* Instant render from LS cache, then refresh from Supabase
       and replace if the fetch succeeded. The LS cache also
       gets rewritten by loadAuditsFromSupabase so a next mount
       sees a fresh snapshot even when offline. */
    setAudits(loadAudits());
    let cancelled = false;
    loadAuditsFromSupabase().then(function (rows) {
      if (!cancelled) setAudits(rows);
    });
    return function () { cancelled = true; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    profileIsAdmin().then(function (b) { if (!cancelled) setIsAdmin(!!b); }).catch(function () {});
    return function () { cancelled = true; };
  }, []);

  const persist = useCallback((next) => {
    setAudits(next);
    saveAudits(next);
  }, []);

  const syncViewing = useCallback((nextAudits) => {
    setViewing(v => v ? (nextAudits.find(a => a.id === v.id) || v) : v);
  }, []);

  const handleSave = (form) => {
    const now = new Date().toISOString();
    if (form.id) {
      const next = audits.map(a => a.id === form.id ? { ...a, ...form, updated_at: now } : a);
      persist(next);
      syncViewing(next);
    } else {
      const id = Date.now();
      /* New audits always start `open`; status only flips via the
         Close / Reopen buttons, never via the form. */
      const rec = { ...form, id, status: "open", reports: [], otherDocs: [], created_at: now, updated_at: now };
      persist([rec, ...audits]);
    }
    setEditing(null);
  };

  const handleDelete = (id) => {
    /* Cleanup IDB blobs so we don't leak storage. */
    const aud = audits.find(a => a.id === id);
    if (aud) {
      (aud.reports || []).forEach(r => { if (r && r.id) deleteReportBlob(r.id); });
      (aud.otherDocs || []).forEach(d => { if (d && d.id) deleteDocBlob(d.id); });
    }
    persist(audits.filter(a => a.id !== id));
    /* Remove the row from Supabase too — persist() only upserts the
       remaining rows, it doesn't know what to delete. */
    deleteAuditFromSupabase(id);
    setViewing(null); setEditing(null); setConfirmDelete(null);
  };

  const handleClose = useCallback(async (id) => {
    const aud = audits.find(a => a.id === id);
    if (!aud) { setConfirmClose(null); return; }
    const closedAt = new Date().toISOString();
    setAudits(prev => {
      const next = prev.map(a => a.id === id ? { ...a, status: "closed", closedAt: closedAt, updated_at: closedAt } : a);
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    setConfirmClose(null);
    /* Stamp the GFI cert with the inspection date + auditor info.
       Failure is logged inside the helper — the audit close itself
       stands regardless. */
    try { await updateGfiCertWithAuditClose({ ...aud, closedAt: closedAt }); } catch (e) { /* logged */ }
  }, [audits, syncViewing]);

  const handleReopen = useCallback((id) => {
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== id) return a;
        return { ...a, status: "open", reopenedAt: new Date().toISOString(), updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    setConfirmReopen(null);
  }, [syncViewing]);

  const handleLogReport = useCallback((auditId, entry) => {
    /* Closed audits can't accept new entries. Panels still surface
       buttons in case a future Reconfigure path opens them, but we
       guard at the persistence boundary too. */
    const target = audits.find(a => a.id === auditId);
    if (target && normalizeStatus(target.status) === "closed") {
      console.warn("[audits] ignored log for closed audit", auditId);
      return;
    }
    const id = newId();
    const blob = entry && entry.blob;
    const persistedEntry = {
      id: id,
      type: entry.type,
      title: entry.title || "",
      generatedAt: new Date().toISOString(),
      recipients: Array.isArray(entry.recipients) ? entry.recipients : [],
      params: entry.params || null,
      hasBlob: !!blob,
      filename: entry.filename || "",
      mime: entry.mime || (blob ? blob.type : "") || "",
    };
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== auditId) return a;
        const reports = Array.isArray(a.reports) ? a.reports.slice() : [];
        reports.unshift(persistedEntry);
        return { ...a, reports: reports, updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    if (blob) {
      saveReportBlob(id, blob).catch(function (e) {
        console.warn("[audits] failed to save report snapshot:", e && e.message);
      });
    }
  }, [audits, syncViewing]);

  const handleDeleteReport = useCallback((auditId, entryId) => {
    if (!window.confirm(t.confirmDelete)) return;
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== auditId) return a;
        return { ...a, reports: (a.reports || []).filter(r => r.id !== entryId), updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    deleteReportBlob(entryId).catch(function () {});
  }, [t.confirmDelete, syncViewing]);

  const handleAddOtherDoc = useCallback((auditId, file, category) => {
    if (!file) return;
    if (file.type && file.type.toLowerCase() !== "application/pdf") {
      window.alert(t.onlyPdf);
      return;
    }
    const id = newId();
    const doc = {
      id: id,
      name: file.name.replace(/\.pdf$/i, ""),
      category: category || "other",
      uploadedAt: new Date().toISOString(),
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || "application/pdf",
    };
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== auditId) return a;
        const docs = Array.isArray(a.otherDocs) ? a.otherDocs.slice() : [];
        docs.unshift(doc);
        return { ...a, otherDocs: docs, updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    saveDocBlob(id, file).catch(function (e) {
      console.warn("[audits] failed to save uploaded doc blob:", e && e.message);
    });
  }, [t.onlyPdf, syncViewing]);

  const handleUpdateOtherDoc = useCallback((auditId, docId, patch) => {
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== auditId) return a;
        return { ...a, otherDocs: (a.otherDocs || []).map(d => d.id === docId ? { ...d, ...patch } : d), updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
  }, [syncViewing]);

  const handleDeleteOtherDoc = useCallback((auditId, docId) => {
    if (!window.confirm(t.confirmDelete)) return;
    setAudits(prev => {
      const next = prev.map(a => {
        if (a.id !== auditId) return a;
        return { ...a, otherDocs: (a.otherDocs || []).filter(d => d.id !== docId), updated_at: new Date().toISOString() };
      });
      saveAudits(next);
      syncViewing(next);
      return next;
    });
    deleteDocBlob(docId).catch(function () {});
  }, [t.confirmDelete, syncViewing]);

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--color-text)" }}>{t.title}</h1>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>{t.sub}</div>
        </div>
        <button onClick={() => setEditing({ name: "", year: new Date().getFullYear(), auditorName: "", auditorEmail: "", startDate: new Date().toISOString().substring(0, 10) })} style={btnPrimary}>{t.btnNew}</button>
      </div>

      {audits.length === 0 ? (
        <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 8 }}>{t.empty}</div>
      ) : (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: 8, overflow: "hidden", background: "var(--color-bg-card)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--color-bg)" }}>
                <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)" }}>{t.colName}</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)", width: 70 }}>{t.colYear}</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)" }}>{t.colAuditor}</th>
                <th style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)", width: 130 }}>{t.colStart}</th>
                <th style={{ textAlign: "center", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)", width: 130 }}>{t.colReports}</th>
                <th style={{ textAlign: "center", padding: "10px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "1px solid var(--color-border)", width: 130 }}>{t.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {audits.map(a => (
                <tr key={a.id} onClick={() => setViewing(a)} style={{ cursor: "pointer", borderBottom: "1px solid var(--color-border)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-bg-hover)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: "var(--color-text)" }}>{a.name || t.none}</td>
                  <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono', monospace" }}>{a.year || t.none}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <div>{a.auditorName || t.none}</div>
                    {a.auditorEmail && <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{a.auditorEmail}</div>}
                  </td>
                  <td style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono', monospace" }}>{fmtDate(a.startDate) || t.none}</td>
                  <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: "var(--color-text)" }}>{(a.reports || []).length}</td>
                  <td style={{ padding: "10px 12px", textAlign: "center" }}><StatusPill status={a.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AuditForm
          lang={lang}
          initial={editing}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {viewing && (
        <AuditDetail
          lang={lang}
          audit={viewing}
          isAdmin={isAdmin}
          onClose={() => setViewing(null)}
          onEdit={() => setEditing(viewing)}
          onOpenReport={(type, initialParams) => setOpenReport({ auditId: viewing.id, type: type, initialParams: initialParams || null })}
          onAddOtherDoc={(file, cat) => handleAddOtherDoc(viewing.id, file, cat)}
          onUpdateOtherDoc={(docId, patch) => handleUpdateOtherDoc(viewing.id, docId, patch)}
          onDeleteOtherDoc={(docId) => handleDeleteOtherDoc(viewing.id, docId)}
          onDeleteReport={(entryId) => handleDeleteReport(viewing.id, entryId)}
          onCloseAudit={() => setConfirmClose(viewing.id)}
          onReopenAudit={() => setConfirmReopen(viewing.id)}
          onDeleteAudit={() => setConfirmDelete(viewing.id)}
        />
      )}

      {openReport && (
        <OpenReportFrame
          openReport={openReport}
          audits={audits}
          lang={lang}
          handleLogReport={handleLogReport}
          setOpenReport={setOpenReport}
        />
      )}

      {confirmClose !== null && (
        <CloseAuditConfirm
          lang={lang}
          onCancel={() => setConfirmClose(null)}
          onConfirm={() => handleClose(confirmClose)}
        />
      )}
      {confirmReopen !== null && (
        <ReopenAuditConfirm
          lang={lang}
          isAdmin={isAdmin}
          onCancel={() => setConfirmReopen(null)}
          onConfirm={() => handleReopen(confirmReopen)}
        />
      )}
      {confirmDelete !== null && (
        <DeleteAuditConfirm
          lang={lang}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDelete(confirmDelete)}
        />
      )}
    </div>
  );
}

/* ============================================================================
 * OpenReportFrame — wrapper that hosts the active report panel with
 * stable callback identities. The previous implementation inlined the
 * panel render inside an IIFE on the parent's JSX, which created
 * fresh `onClose` / `onLogReport` function references on every parent
 * re-render. Those new refs propagated into the panel and (more
 * importantly) into DraggableModal's title/zIndex props — each
 * re-render flashed the modal title bar and any child that compared
 * function refs by identity. Hoisting the callbacks into a useCallback
 * keyed by `auditId` makes them stable across renders and kills the
 * flicker.
 * ========================================================================= */
function OpenReportFrame({ openReport, audits, lang, handleLogReport, setOpenReport }) {
  const audit = useMemo(
    function () { return audits.find(function (a) { return a.id === openReport.auditId; }) || null; },
    [audits, openReport.auditId]
  );
  const auditId = audit && audit.id;
  const onClose = useCallback(function () { setOpenReport(null); }, [setOpenReport]);
  const onLogReport = useCallback(
    function (entry) { return handleLogReport(auditId, entry); },
    [auditId, handleLogReport]
  );
  if (!audit) return null;
  const ip = openReport.initialParams;
  if (openReport.type === "mass_balance")   return <MassBalancePanel   lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "organic_certs")  return <OrganicCertsPanel  lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "traceability")   return <TraceabilityPanel  lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "organic_labels") return <OrganicLabelsPanel lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "general_info")     return <GeneralInfoPanel          lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "traceability_proc") return <TraceabilityProcedurePanel lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "audit_summary") return <AuditSummaryPanel lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  if (openReport.type === "complaints_proc") return <ComplaintsProcedurePanel lang={lang} audit={audit} onClose={onClose} onLogReport={onLogReport} initialParams={ip} />;
  return null;
}

/* ============================================================================
 * AuditForm — create / edit modal. No status field — open/closed is
 * controlled exclusively by the Close + Reopen buttons in the detail.
 * ========================================================================= */
function AuditForm({ lang, initial, onSave, onCancel }) {
  const t = T[lang === "es" ? "es" : "en"];
  const isNew = !initial || !initial.id;
  const [form, setForm] = useState(() => ({ ...initial }));
  const [errors, setErrors] = useState({});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const validate = () => {
    const e = {};
    if (!form.name) e.name = lang === "es" ? "Requerido" : "Required";
    if (!form.year) e.year = lang === "es" ? "Requerido" : "Required";
    if (!form.auditorName) e.auditorName = lang === "es" ? "Requerido" : "Required";
    if (!form.auditorEmail) e.auditorEmail = lang === "es" ? "Requerido" : "Required";
    else if (!isValidEmail(form.auditorEmail)) e.auditorEmail = (lang === "es" ? "Correo inválido" : "Invalid email");
    if (!form.startDate) e.startDate = lang === "es" ? "Requerido" : "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <DraggableModal title={isNew ? t.formNew : t.formEdit} onClose={onCancel} zIndex={10005}>
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
        <Field label={t.fName} error={errors.name}>
          <input value={form.name || ""} onChange={e => set("name", e.target.value)} style={inp} placeholder="Organic Audit 2026" />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t.fYear} error={errors.year}>
            <input type="number" value={form.year || ""} onChange={e => set("year", e.target.value ? parseInt(e.target.value, 10) : "")} style={inp} />
          </Field>
          <Field label={t.fStart} error={errors.startDate}>
            <input type="date" value={form.startDate || ""} onChange={e => set("startDate", e.target.value)} style={inp} />
          </Field>
        </div>
        <Field label={t.fAuditorName} error={errors.auditorName}>
          <input value={form.auditorName || ""} onChange={e => set("auditorName", e.target.value)} style={inp} />
        </Field>
        <Field label={t.fAuditorEmail} error={errors.auditorEmail}>
          <input type="email" value={form.auditorEmail || ""} onChange={e => set("auditorEmail", e.target.value)} style={inp} placeholder="auditor@certifier.com" />
        </Field>
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={btnSecondary}>{t.cancel}</button>
        <button onClick={() => { if (validate()) onSave(form); }} style={btnPrimary}>{t.save}</button>
      </div>
    </DraggableModal>
  );
}

/* ============================================================================
 * AuditDetail — single-audit modal: report cards, request history,
 * other-documents shelf, multi-select email, GFI cert strip, and
 * the open/closed state controls.
 * ========================================================================= */
function AuditDetail({
  lang, audit, isAdmin,
  onClose, onEdit, onOpenReport,
  onAddOtherDoc, onUpdateOtherDoc, onDeleteOtherDoc, onDeleteReport,
  onCloseAudit, onReopenAudit, onDeleteAudit,
}) {
  const t = T[lang === "es" ? "es" : "en"];
  const closed = normalizeStatus(audit.status) === "closed";

  const reportCards = useMemo(() => ([
    { type: "mass_balance",   title: t.repMass,   sub: t.repMassSub,   icon: "⚖" },
    { type: "organic_certs",  title: t.repCerts,  sub: t.repCertsSub,  icon: "🏷" },
    { type: "traceability",   title: t.repTrace,  sub: t.repTraceSub,  icon: "🔗" },
    { type: "organic_labels", title: t.repLabels, sub: t.repLabelsSub, icon: "📷" },
    { type: "general_info",     title: t.repGenInfo, sub: t.repGenInfoSub, icon: "📋" },
    { type: "traceability_proc", title: t.repTrace2,  sub: t.repTrace2Sub,  icon: "🏷" },
    { type: "audit_summary", title: (lang === "es" ? "Reporte Resumen de Auditoría" : "Audit Summary Report"), sub: (lang === "es" ? "Índice de reportes generados y documentos cargados" : "Index of generated reports and uploaded documents"), icon: "📑" },
    { type: "complaints_proc", title: t.repComplaints, sub: t.repComplaintsSub, icon: "📨" },
  ]), [t, lang]);
  const labelFor = (type) => {
    const c = reportCards.find(r => r.type === type);
    return c ? c.title : type;
  };
  const reports   = Array.isArray(audit.reports)   ? audit.reports   : [];
  const otherDocs = Array.isArray(audit.otherDocs) ? audit.otherDocs : [];

  /* Re-read the GFI cert each render — cheap (parse a few KB of JSON)
     and ensures we pick up edits made in the Certifications module
     while this modal is open. */
  const gfiCert = useMemo(() => loadGfiOrganicCert(), [audit.id, audit.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pickedReports, setPickedReports] = useState({});
  const [pickedDocs, setPickedDocs]       = useState({});
  const [bulkEmail, setBulkEmail] = useState(null);

  const toggleReport = (id) => setPickedReports(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleDoc    = (id) => setPickedDocs(prev =>    ({ ...prev, [id]: !prev[id] }));

  const pickedReportIds = useMemo(() => Object.keys(pickedReports).filter(k => pickedReports[k]), [pickedReports]);
  const pickedDocIds    = useMemo(() => Object.keys(pickedDocs).filter(k => pickedDocs[k]), [pickedDocs]);
  const totalPicked = pickedReportIds.length + pickedDocIds.length;

  const handleViewSnapshot = async (entry) => {
    if (!entry || !entry.hasBlob) return;
    const blob = await loadReportBlob(entry.id);
    if (blob) openBlobInTab(blob, entry.filename || "report.pdf");
    else window.alert(lang === "es" ? "El snapshot del PDF ya no está disponible." : "The PDF snapshot is no longer available.");
  };
  const handleDownloadSnapshot = async (entry) => {
    if (!entry || !entry.hasBlob) return;
    const blob = await loadReportBlob(entry.id);
    if (!blob) { window.alert(lang === "es" ? "El snapshot del PDF ya no está disponible." : "The PDF snapshot is no longer available."); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = entry.filename || "report.pdf";
    document.body.appendChild(a); a.click(); setTimeout(function () { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
  };
  const handleReopen = (entry) => {
    if (entry.hasBlob) { handleViewSnapshot(entry); return; }
    onOpenReport(entry.type, entry.params || null);
  };
  const handleReconfigure = (entry) => {
    onOpenReport(entry.type, entry.params || null);
  };
  const handleViewDoc = async (doc) => {
    const blob = await loadDocBlob(doc.id);
    if (blob) openBlobInTab(blob, doc.fileName || (doc.name + ".pdf"));
    else window.alert(lang === "es" ? "El archivo no está disponible." : "File not available.");
  };
  const handleDownloadDoc = async (doc) => {
    const blob = await loadDocBlob(doc.id);
    if (!blob) { window.alert(lang === "es" ? "El archivo no está disponible." : "File not available."); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = doc.fileName || (doc.name + ".pdf");
    document.body.appendChild(a); a.click(); setTimeout(function () { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
  };

  const emailOneReport = (entry) => setBulkEmail({ kind: "single", reportIds: [entry.id], docIds: [] });
  const emailOneDoc    = (doc)   => setBulkEmail({ kind: "single", reportIds: [], docIds: [doc.id] });
  const emailSelected  = ()      => setBulkEmail({ kind: "selected", reportIds: pickedReportIds, docIds: pickedDocIds });
  const emailWhole     = ()      => {
    if (reports.length === 0 && otherDocs.length === 0) { window.alert(t.nothingToEmail); return; }
    setBulkEmail({ kind: "whole", reportIds: reports.map(r => r.id), docIds: otherDocs.map(d => d.id) });
  };

  return (
    <DraggableModal title={(audit.name || t.title) + (closed ? "  ·  " + t.statusClosed : "")} onClose={onClose} zIndex={10001}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {/* Header strip */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, auto)", gap: "6px 22px", fontSize: 12 }}>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{t.colYear}</div>
              <div style={{ fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{audit.year || t.none}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{t.colAuditor}</div>
              <div style={{ fontWeight: 600 }}>{audit.auditorName || t.none}</div>
              {audit.auditorEmail && <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{audit.auditorEmail}</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{t.colStart}</div>
              <div style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{fmtDate(audit.startDate) || t.none}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{t.colStatus}</div>
              <div style={{ marginTop: 2 }}><StatusPill status={audit.status} t={t} /></div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button onClick={emailWhole} style={btnPrimary}>{t.emailWholeAudit}</button>
            {!closed && <button onClick={onEdit} style={btnSecondary}>{t.edit}</button>}
            {!closed && <button onClick={onCloseAudit} style={btnSecondary}>{t.btnClose}</button>}
            {closed && isAdmin && <button onClick={onReopenAudit} style={btnSecondary}>{t.btnReopen}</button>}
            {!closed && <button onClick={onDeleteAudit} style={btnDanger}>{t.delete}</button>}
          </div>
        </div>

        {/* GFI cert reference strip */}
        <div style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", marginBottom: 22 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.gfiCertTitle}</div>
          {!gfiCert ? (
            <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.gfiCertMissing}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, auto)", gap: "4px 22px", fontSize: 12 }}>
              <GfiCertCell label={t.gfiCertNumber}   value={gfiCert.certNumber} mono />
              <GfiCertCell label={t.gfiCertNop}      value={gfiCert.nopId} mono />
              <GfiCertCell label={t.gfiCertCertifier} value={gfiCert.certifier === "__other__" ? gfiCert.certifierOther : gfiCert.certifier} />
              <GfiCertCell label={t.gfiCertValid}    value={(fmtDate(gfiCert.validFrom) || "—") + "  →  " + (fmtDate(gfiCert.anniversaryDate) || fmtDate(gfiCert.validTo) || "—")} mono />
              <GfiCertCell label={t.gfiCertLastInsp} value={fmtDate(gfiCert.inspectionDate) || t.none} mono />
            </div>
          )}
        </div>

        {/* Report cards */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>{t.reports}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginBottom: 24 }}>
          {reportCards.map(card => (
            <div key={card.type} style={{ padding: 16, borderRadius: 10, border: "1px solid var(--color-border)", background: "var(--color-bg-card)", display: "flex", flexDirection: "column", opacity: closed ? 0.6 : 1 }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{card.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "var(--color-text)", marginBottom: 4 }}>{card.title}</div>
              <div style={{ fontSize: 11.5, color: "var(--color-text-muted)", lineHeight: 1.45, flex: 1 }}>{card.sub}</div>
              <button
                onClick={() => onOpenReport(card.type, null)}
                disabled={closed}
                title={closed ? t.closedNoGenerate : undefined}
                style={{ ...btnPrimary, marginTop: 12, alignSelf: "flex-start", cursor: closed ? "not-allowed" : "pointer", opacity: closed ? 0.6 : 1 }}
              >{t.generate}</button>
            </div>
          ))}
        </div>

        {/* Request history */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t.historyTitle}</div>
          {totalPicked > 0 && (
            <button onClick={emailSelected} style={btnSecondary}>{t.emailSelected} ({totalPicked})</button>
          )}
        </div>
        {reports.length === 0 ? (
          <div style={{ padding: 18, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 6, fontSize: 12, marginBottom: 22 }}>{t.historyEmpty}</div>
        ) : (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", marginBottom: 22 }}>
            {reports.map(r => (
              <ReportRow
                key={r.id} lang={lang} entry={r} labelFor={labelFor}
                checked={!!pickedReports[r.id]} onToggle={() => toggleReport(r.id)}
                onReopen={() => handleReopen(r)}
                onReconfigure={() => handleReconfigure(r)}
                onDownload={() => handleDownloadSnapshot(r)}
                onEmail={() => emailOneReport(r)}
                onDelete={() => onDeleteReport(r.id)}
                readOnly={closed}
              />
            ))}
          </div>
        )}

        {/* Other documents */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{t.docsTitle}</div>
        <OtherDocsSection
          lang={lang}
          docs={otherDocs}
          pickedDocs={pickedDocs}
          onToggle={toggleDoc}
          onUpload={onAddOtherDoc}
          onUpdate={onUpdateOtherDoc}
          onDelete={onDeleteOtherDoc}
          onView={handleViewDoc}
          onDownload={handleDownloadDoc}
          onEmail={emailOneDoc}
          readOnly={closed}
        />

        <div style={{ marginTop: 24 }}>
          <AuditComments
            auditId={audit.id}
            targetType="audit"
            targetId={null}
            author={{ name: "GFI", role: "admin" }}
            lang={lang}
          />
        </div>
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end" }}>
        <button onClick={onClose} style={btnSecondary}>{t.close}</button>
      </div>

      {bulkEmail && (
        <BulkEmailComposer
          lang={lang}
          audit={audit}
          intent={bulkEmail}
          onClose={() => setBulkEmail(null)}
        />
      )}
    </DraggableModal>
  );
}

function GfiCertCell({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)" }}>{label}</div>
      <div style={{ fontWeight: 600, fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit", fontSize: 11.5 }}>{value || "—"}</div>
    </div>
  );
}

function ReportRow({ lang, entry, labelFor, checked, onToggle, onReopen, onReconfigure, onDownload, onEmail, onDelete, readOnly }) {
  const t = T[lang === "es" ? "es" : "en"];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "28px 160px 1fr 140px auto", gap: 10, padding: "8px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 12, alignItems: "center" }}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <div>
        <div style={{ fontWeight: 700, color: "var(--color-text)" }}>{labelFor(entry.type)}</div>
        {!entry.hasBlob && <div style={{ fontSize: 9.5, color: "#999", fontStyle: "italic" }}>{t.noSnapshot}</div>}
      </div>
      <div style={{ color: "var(--color-text-muted)" }}>
        {entry.title && <div>{entry.title}</div>}
        {entry.params && <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 1 }}>{t.historyParams}: {paramSummary(entry.type, entry.params, lang)}</div>}
        {Array.isArray(entry.recipients) && entry.recipients.length > 0 && (
          <div style={{ fontSize: 11, marginTop: 2 }}>{t.historyRecipients}: <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{entry.recipients.join(", ")}</span></div>
        )}
      </div>
      <div style={{ color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{fmtDateTime(entry.generatedAt)}</div>
      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
        {entry.hasBlob && <button onClick={onReopen} style={btnTiny}>{t.actView}</button>}
        {!readOnly && <button onClick={onReconfigure} style={btnTiny}>{t.actReconfigure}</button>}
        {entry.hasBlob && <button onClick={onDownload} style={btnTiny}>{t.actDownload}</button>}
        <button onClick={onEmail} style={btnTiny}>{t.actEmail}</button>
        {!readOnly && <button onClick={onDelete} style={{ ...btnTiny, color: "#DC2626", borderColor: "rgba(220,38,38,0.30)" }}>{t.actDelete}</button>}
      </div>
    </div>
  );
}

function OtherDocsSection({ lang, docs, pickedDocs, onToggle, onUpload, onUpdate, onDelete, onView, onDownload, onEmail, readOnly }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [pendingCategory, setPendingCategory] = useState("other");

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    Array.from(fileList).forEach(f => onUpload(f, pendingCategory));
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    if (readOnly) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {!readOnly && (
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={{
            border: "2px dashed " + (dragOver ? "var(--color-accent)" : "var(--color-border)"),
            borderRadius: 8, padding: "16px 20px",
            background: dragOver ? "rgba(42,122,111,0.06)" : "var(--color-bg-card)",
            marginBottom: 12,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
          }}
        >
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.docsDrag}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={pendingCategory} onChange={e => setPendingCategory(e.target.value)} style={{ ...inp, fontFamily: "inherit", width: "auto", padding: "6px 8px", fontSize: 12 }}>
              <option value="pre">{t.cat_pre}</option>
              <option value="received">{t.cat_received}</option>
              <option value="other">{t.cat_other}</option>
            </select>
            <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={btnSecondary}>{t.docsUpload}</button>
            <input type="file" ref={fileInputRef} multiple accept="application/pdf,.pdf" style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <div style={{ padding: 18, textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", border: "1px dashed var(--color-border)", borderRadius: 6, fontSize: 12 }}>{readOnly ? t.docsEmptyClosed : t.docsEmpty}</div>
      ) : (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden" }}>
          {docs.map(d => (
            <div key={d.id} style={{ display: "grid", gridTemplateColumns: "28px 1.5fr 160px 140px auto", gap: 10, padding: "8px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 12, alignItems: "center" }}>
              <input type="checkbox" checked={!!pickedDocs[d.id]} onChange={() => onToggle(d.id)} />
              <div>
                <input
                  value={d.name || ""}
                  onChange={e => onUpdate(d.id, { name: e.target.value })}
                  placeholder={t.docsName}
                  readOnly={readOnly}
                  style={{ ...inp, padding: "5px 8px", fontSize: 12, fontWeight: 600, background: readOnly ? "transparent" : "var(--color-bg-input)" }}
                />
                <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>
                  {d.fileName} · {fmtFileSize(d.fileSize)}
                </div>
              </div>
              <select value={d.category || "other"} onChange={e => onUpdate(d.id, { category: e.target.value })} disabled={readOnly} style={{ ...inp, fontFamily: "inherit", padding: "5px 8px", fontSize: 11.5 }}>
                <option value="pre">{t.cat_pre}</option>
                <option value="received">{t.cat_received}</option>
                <option value="other">{t.cat_other}</option>
              </select>
              <div style={{ color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{fmtDateTime(d.uploadedAt)}</div>
              <div style={{ display: "flex", gap: 4, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button onClick={() => onView(d)} style={btnTiny}>{t.actView}</button>
                <button onClick={() => onDownload(d)} style={btnTiny}>{t.actDownload}</button>
                <button onClick={() => onEmail(d)} style={btnTiny}>{t.actEmail}</button>
                {!readOnly && <button onClick={() => onDelete(d.id)} style={{ ...btnTiny, color: "#DC2626", borderColor: "rgba(220,38,38,0.30)" }}>{t.actDelete}</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Confirm modals — close / reopen / delete.
 * ========================================================================= */
function CloseAuditConfirm({ lang, onCancel, onConfirm }) {
  const t = T[lang === "es" ? "es" : "en"];
  return (
    <DraggableModal title={t.closeAuditTitle} onClose={onCancel} zIndex={10010}>
      <div style={{ padding: "20px 24px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.55 }}>
        {t.closeAuditMsg}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={btnSecondary}>{t.cancel}</button>
        <button onClick={onConfirm} style={btnPrimary}>{t.closeAuditConfirm}</button>
      </div>
    </DraggableModal>
  );
}

function ReopenAuditConfirm({ lang, isAdmin, onCancel, onConfirm }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!isAdmin) {
    /* Belt-and-suspenders: button is already hidden for non-admins,
       but show a stub modal in case some other path routes here. */
    return (
      <DraggableModal title={t.reopenAuditTitle} onClose={onCancel} zIndex={10010}>
        <div style={{ padding: 24, fontSize: 13, color: "#991B1B" }}>{t.onlyAdminCanReopen}</div>
        <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={btnSecondary}>{t.close}</button>
        </div>
      </DraggableModal>
    );
  }
  const submit = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const ok = await verifyCurrentUserPassword(password);
      if (!ok) { setError(t.badPassword); setBusy(false); return; }
      onConfirm();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };
  return (
    <DraggableModal title={t.reopenAuditTitle} onClose={onCancel} zIndex={10010}>
      <div style={{ padding: "20px 24px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.55 }}>
        <div style={{ marginBottom: 14 }}>{t.reopenAuditMsg}</div>
        <Field label={t.passwordLabel}>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submit(); }}
            autoFocus
            style={inp}
          />
        </Field>
        {error && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 6 }}>{error}</div>}
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={btnSecondary} disabled={busy}>{t.cancel}</button>
        <button onClick={submit} style={btnPrimary} disabled={busy || !password}>{busy ? "..." : t.reopenAuditConfirm}</button>
      </div>
    </DraggableModal>
  );
}

function DeleteAuditConfirm({ lang, onCancel, onConfirm }) {
  const t = T[lang === "es" ? "es" : "en"];
  /* Two-step: first explanatory copy, then a literal "DELETE" /
     "ELIMINAR" typed confirmation so the user has to slow down. */
  const [typed, setTyped] = useState("");
  const expected = t.deleteAuditConfirm;
  const matches = typed.trim().toUpperCase() === expected.toUpperCase();
  return (
    <DraggableModal title={t.deleteAuditTitle} onClose={onCancel} zIndex={10010}>
      <div style={{ padding: "20px 24px", fontSize: 13, color: "var(--color-text)", lineHeight: 1.55 }}>
        <div style={{ marginBottom: 12 }}>{t.deleteAuditMsg1}</div>
        <div style={{ marginBottom: 12, fontWeight: 600 }}>{t.deleteAuditMsg2}</div>
        <input
          type="text"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={expected}
          autoFocus
          style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 2 }}
        />
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={btnSecondary}>{t.cancel}</button>
        <button onClick={() => matches && onConfirm()} style={{ ...btnDanger, opacity: matches ? 1 : 0.5, cursor: matches ? "pointer" : "not-allowed" }} disabled={!matches}>{t.delete}</button>
      </div>
    </DraggableModal>
  );
}

/* ============================================================================
 * BulkEmailComposer — single email, many attachments. Used by:
 *   - the per-row Email button (intent.kind = "single")
 *   - the "Email Selected" toolbar action (intent.kind = "selected")
 *   - the "Email Whole Audit" header button (intent.kind = "whole")
 *
 * On open, async-loads every requested blob from IDB. Reports without
 * a snapshot are skipped silently (their entry is logged but the
 * blob was never persisted — usually an old entry pre-snapshot
 * feature). The user sees the resolved attachment list before send.
 * ========================================================================= */
function BulkEmailComposer({ lang, audit, intent, onClose }) {
  const t = T[lang === "es" ? "es" : "en"];
  const [attachments, setAttachments] = useState(null);
  const [error, setError] = useState("");

  const [emailProfile, setEmailProfile] = useState({});
  useEffect(() => {
    let alive = true;
    loadFromSupabase("company_profile")
      .then(function (rows) {
        if (!alive) return;
        var p = (rows || []).find(function (r) { return String(r.id) === "gfi"; }) || (rows || [])[0] || {};
        setEmailProfile(p);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, []);
  const profile = emailProfile;
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail  = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 (Arturo: "ME APARECEN NUEVAMENTE 4 CUENTAS DE CORREOS
     DE SALIDA") — this always sends organic-audit documents to the
     auditor from Ground-Fresh's real named account, never Sweetleaf
     or the automated accounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  useEffect(() => {
    let cancelled = false;
    (async function () {
      try {
        const reportBlobs = await loadReportBlobs(intent.reportIds || []);
        const docBlobs    = await loadDocBlobs(intent.docIds || []);
        if (cancelled) return;
        const out = [];
        (audit.reports || []).forEach(function (r) {
          if (!intent.reportIds || intent.reportIds.indexOf(r.id) < 0) return;
          const blob = reportBlobs[String(r.id)];
          if (!blob) return;
          out.push({ filename: r.filename || (r.type + ".pdf"), blob: blob, mimeType: r.mime || "application/pdf" });
        });
        (audit.otherDocs || []).forEach(function (d) {
          if (!intent.docIds || intent.docIds.indexOf(d.id) < 0) return;
          const blob = docBlobs[String(d.id)];
          if (!blob) return;
          out.push({ filename: d.fileName || ((d.name || "document") + ".pdf"), blob: blob, mimeType: d.fileType || "application/pdf" });
        });
        setAttachments(out);
      } catch (e) {
        console.error("[BulkEmail] resolve failed:", e);
        setError(e.message || String(e));
      }
    })();
    return function () { cancelled = true; };
  }, [intent, audit]);

  const subject = useMemo(() => {
    if (intent.kind === "whole") return t.bulkEmailSubject + " — " + (audit.name || "");
    if (intent.kind === "selected") return t.emailSelected + " — " + (audit.name || "");
    return (audit.name || t.title);
  }, [intent, audit, t]);

  const body = useMemo(() => {
    if (lang === "es") {
      return "Adjunto los documentos solicitados para la auditoría " + (audit.name || "") + ".";
    }
    return "Attached are the requested documents for audit " + (audit.name || "") + ".";
  }, [audit, lang]);

  if (error) {
    return (
      <DraggableModal title={t.actEmail} onClose={onClose} zIndex={10006}>
        <div style={{ padding: 24, fontSize: 13, color: "#991B1B" }}>{error}</div>
        <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
        </div>
      </DraggableModal>
    );
  }
  if (attachments === null) {
    return (
      <DraggableModal title={t.actEmail} onClose={onClose} zIndex={10006}>
        <div style={{ padding: 32, fontSize: 13, color: "var(--color-text-muted)", textAlign: "center" }}>
          {lang === "es" ? "Cargando adjuntos..." : "Loading attachments..."}
        </div>
      </DraggableModal>
    );
  }

  const docOptions = attachments.map(function (a, i) {
    return { key: String(i), label: a.filename, checked: true };
  });

  return (
    <EmailDialog
      lang={lang}
      title={t.actEmail}
      recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
      docOptions={docOptions}
      defaultSubject={subject}
      defaultBody={body}
      fromEmail={fromEmail}
      emailAccounts={emailAccounts}
      signatures={signatures}
      /* 2026-09-28 (Arturo: "revisa todos los emails que se envian de
         ground-fresh e inclutye al final el CONFIDENTIALITY NOTICE si
         les hace falta, todos la deben contener") — this sends
         Ground-Fresh's own organic-audit documents to the auditor,
         always Ground-Fresh, never SweetLeaf. */
      postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
      buildAttachments={async (checkedKeys) => {
        return attachments.filter(function (_, i) { return checkedKeys.indexOf(String(i)) >= 0; });
      }}
      onSent={() => { onClose(); }}
      onClose={onClose}
    />
  );
}

export { T as ORGANIC_AUDIT_T };
